import { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";

function pad(n) {
  return n.toString().padStart(2, "0");
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfGrid(d) {
  const s = startOfMonth(d);
  s.setDate(s.getDate() - s.getDay());
  return s;
}
function isoLocalFromDate(d) {
  // local-time ISO without timezone, suitable for stable round-trip
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}
function parseAny(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}
function fmt(d) {
  if (!d) return "";
  return `${d.toLocaleDateString('en-GB', { month: "short", day: "numeric", year: "numeric" })} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date & time",
  testId,
  showTime = true,
  required = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => parseAny(value) || new Date());
  const [hour, setHour] = useState(() => (parseAny(value) ? parseAny(value).getHours() : 9));
  const [minute, setMinute] = useState(() => (parseAny(value) ? parseAny(value).getMinutes() : 0));
  const [selected, setSelected] = useState(() => parseAny(value));
  const wrapRef = useRef(null);

  useEffect(() => {
    const d = parseAny(value);
    setSelected(d);
    if (d) {
      setCursor(d);
      setHour(d.getHours());
      setMinute(d.getMinutes());
    }
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const days = (() => {
    const start = startOfGrid(cursor);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  })();

  const apply = () => {
    if (!selected) return;
    const d = new Date(selected);
    if (showTime) {
      d.setHours(hour);
      d.setMinutes(minute);
      d.setSeconds(0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    onChange(isoLocalFromDate(d));
    setOpen(false);
  };

  const clear = () => {
    setSelected(null);
    onChange("");
    setOpen(false);
  };

  const todayBtn = () => {
    const d = new Date();
    setSelected(d);
    setCursor(d);
    if (showTime) {
      setHour(d.getHours());
      setMinute(d.getMinutes());
    }
  };

  const display = selected ? (showTime ? fmt(selected) : selected.toLocaleDateString('en-GB')) : "";

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={testId}
        className={`hv-input w-full flex items-center justify-between gap-2 text-left ${
          display ? "text-white" : "text-[#647087]"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {showTime ? (
            <Clock className="w-4 h-4 text-[#1fb6c1]" />
          ) : (
            <CalendarIcon className="w-4 h-4 text-[#1fb6c1]" />
          )}
          {display || placeholder}
        </span>
        {selected && !required && (
          <X
            className="w-3.5 h-3.5 text-[#647087] hover:text-rose-300"
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
          />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-[300px] bg-[#0a0b0e] border border-white/15 rounded-md shadow-2xl shadow-black/60 p-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="p-1.5 hover:bg-white/5 rounded"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-medium">
              {cursor.toLocaleString('en-GB', { month: "long", year: "numeric" })}
            </div>
            <button
              type="button"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="p-1.5 hover:bg-white/5 rounded"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 hv-keep-cols text-[10px] uppercase tracking-wider text-[#647087] mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={i} className="text-center py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 hv-keep-cols gap-0.5">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isSel =
                selected &&
                d.getFullYear() === selected.getFullYear() &&
                d.getMonth() === selected.getMonth() &&
                d.getDate() === selected.getDate();
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => setSelected(d)}
                  className={`text-xs py-1.5 rounded transition ${
                    isSel
                      ? "bg-[#1fb6c1] text-black font-bold"
                      : isToday
                      ? "border border-[#1fb6c1] text-[#1fb6c1]"
                      : inMonth
                      ? "text-white hover:bg-white/10"
                      : "text-[#647087] hover:bg-white/5"
                  }`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {showTime && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Time
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={hour}
                  onChange={(e) => setHour(parseInt(e.target.value))}
                  className="hv-input flex-1 py-1 text-sm"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {pad(h)}
                    </option>
                  ))}
                </select>
                <span className="text-[#647087]">:</span>
                <select
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value))}
                  className="hv-input flex-1 py-1 text-sm"
                >
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <option key={m} value={m}>
                      {pad(m)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={todayBtn}
              className="hv-btn hv-btn-ghost text-xs flex-1"
            >
              Today
            </button>
            {!required && (
              <button
                type="button"
                onClick={clear}
                className="hv-btn hv-btn-ghost text-xs flex-1"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={apply}
              disabled={!selected}
              className="hv-btn hv-btn-primary text-xs flex-1 disabled:opacity-50"
            >
              Set
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
